import { Link } from 'react-router';
import type { PublicPack } from '@/types';
import { Price } from '@/components/Price';
import { FramedImage } from '@/components/FramedImage';
import { PackCollage } from '@/components/PackCollage';
import { useI18n } from '@/client/i18n/I18nContext';
import { PACK_PATH, packName } from '@/lib/catalog/packs';
import { localizeProduct } from '@/lib/i18n-content';
import { lp } from '@/client/i18n/locale';

/**
 * A pack in a shop grid, the same quiet card as ProductCard: photo (its own or
 * the components' collage), «Pack −10 %», name and price now / before (the
 * API's, never computed here). The whole card links to the pack page.
 */
export function PackCard({ pack }: { pack: PublicPack }) {
  const { t, locale } = useI18n();
  const name = packName(pack, locale);
  const href = lp(`${PACK_PATH}/${pack.slug}`);
  const covers = pack.items.flatMap((it) => {
    const c = pack.components.find((p) => p.id === it.productId);
    return c ? [localizeProduct(c, locale)] : [];
  });
  const currency = pack.components[0]?.currency ?? 'EUR';

  return (
    <article className="card card--pack">
      <Link to={href} className="card__media" aria-label={name}>
        {pack.image ? (
          <FramedImage src={pack.image} alt={name} loading="lazy" fallback="CARO" />
        ) : (
          <PackCollage items={covers} alt="" loading="lazy" sizes="(min-width: 768px) 10vw, 17vw" />
        )}
      </Link>
      <Link to={href} className="card__body">
        <span className="card__pack">{t('pack.badge').replace('{pct}', String(pack.discountPercent))}</span>
        <h3 className="card__name">{name}</h3>
        <div className="card__footer">
          <Price cents={pack.priceCents} currency={currency} compareAtCents={pack.compareAtCents} />
        </div>
      </Link>
    </article>
  );
}
